import { api } from '@/lib/api';
import type { NotificationItem } from '@/types/api';

/* ============================================================================
 * Notifications API. Mirrors backend/messaging/views.py:
 *   GET    /notifications/?unread_only=&limit=
 *   POST   /notifications/{id}/read/
 *   POST   /notifications/read-all/
 *   DELETE /notifications/{id}/
 *   DELETE /notifications/clear-read/
 * ==========================================================================*/

/** Raw backend shape — field names differ from the UI's NotificationItem. */
interface RawNotification {
  id: number;
  notification_type?: string;
  kind?: string;
  title: string;
  message?: string;
  body?: string;
  is_read?: boolean;
  read?: boolean;
  link_url?: string | null;
  link?: string | null;
  created_at: string;
}

/** Normalize a backend notification to the UI's NotificationItem shape. */
function normalize(raw: RawNotification): NotificationItem {
  return {
    id: raw.id,
    kind: raw.notification_type ?? raw.kind ?? 'general',
    title: raw.title,
    body: raw.message ?? raw.body ?? '',
    link: raw.link_url ?? raw.link ?? null,
    read: raw.is_read ?? raw.read ?? false,
    created_at: raw.created_at,
  };
}

export interface NotificationList {
  items: NotificationItem[];
  unreadCount: number;
}

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number }): Promise<NotificationList> =>
    api
      .get<{ count?: number; unread_count?: number; notifications?: RawNotification[] }>(
        '/notifications/',
        {
          params: {
            unread_only: params?.unread_only ? 'true' : undefined,
            limit: params?.limit,
          },
        },
      )
      .then((r) => ({
        items: (r.data.notifications ?? []).map(normalize),
        unreadCount: r.data.unread_count ?? 0,
      })),

  markRead: (id: number) => api.post(`/notifications/${id}/read/`).then((r) => r.data),
  markAllRead: () => api.post('/notifications/read-all/').then((r) => r.data),
  remove: (id: number) => api.delete(`/notifications/${id}/`).then((r) => r.data),
  clearRead: () => api.delete('/notifications/clear-read/').then((r) => r.data),
};
